/**
 * intelligenceApi.js
 *
 * Thin client for the Happy AI cost-intelligence (`/ai/cost-intelligence/*`) and
 * stock-intelligence (`/ai/stock-intelligence/*`) endpoints. Cloned from
 * `helpers/businessAiApi.js`: every export normalizes to `{ok, data, error}` and
 * NEVER throws; the active store comes from the same management scope
 * (`restaurantId` query + `x-restaurant-id` header). `status` carries the HTTP
 * status when the transport error exposes it (422 on the prep-planning date).
 *
 * @module helpers/intelligenceApi
 */
import {apiFetch} from './WorkSheetContext';

const scopeHeaders = (scope) => (scope?.restaurantId ? {'x-restaurant-id': String(scope.restaurantId)} : {});

/**
 * Build a query string, skipping empty values and `false` flags.
 * @param {Record<string, *>} params raw params.
 * @param {{restaurantId?: string}} [scope] active scope (adds `restaurantId`).
 * @returns {string} `''` or `?a=b&c=d`.
 */
export const buildQuery = (params, scope) => {
    const query = new globalThis.URLSearchParams();

    if (scope?.restaurantId) query.set('restaurantId', String(scope.restaurantId));
    Object.keys(params || {}).forEach((key) => {
        const value = params[key];

        if (value === undefined || value === null || value === '' || value === false) return;
        query.set(key, String(value));
    });
    const text = query.toString();

    return text ? `?${text}` : '';
};

const run = async (path, scope, fallbackError) => {
    try {
        const data = await apiFetch(path, {headers: scopeHeaders(scope), skipCache: true});

        return {ok: true, data: data || null, error: null, status: 200};
    } catch (error) {
        return {ok: false, data: null, error: error?.message || fallbackError, status: Number(error?.status || error?.statusCode) || null};
    }
};

const get = (area, name, params, scope, fallbackError) => run(`/ai/${area}/${name}${buildQuery(params, scope)}`, scope, fallbackError);

/**
 * Product margins.
 * @param {{days?: number, sort?: string, limit?: number, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getMargins = (params = {}, scope) => get('cost-intelligence', 'margins', {days: 30, ...params}, scope, 'cost_margins_failed');

/**
 * Ingredient price changes.
 * @param {{days?: number, minChangePct?: number, limit?: number, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getIngredientPrices = (params = {}, scope) => get('cost-intelligence', 'ingredient-prices', {days: 30, minChangePct: 5, ...params}, scope, 'cost_prices_failed');

/**
 * ABC analysis.
 * @param {{days?: number, basis?: 'products'|'stock', limit?: number, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getAbc = (params = {}, scope) => get('cost-intelligence', 'abc', {days: 90, basis: 'products', ...params}, scope, 'cost_abc_failed');

/**
 * Order forecast.
 * @param {{horizonDays?: number, limit?: number, onlyRisk?: boolean, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getStockForecast = (params = {}, scope) => get('stock-intelligence', 'forecast', {horizonDays: 7, limit: 50, ...params}, scope, 'stock_forecast_failed');

/**
 * Product (prep) forecast for one day.
 * @param {{date?: string, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null), status: ?number}>}
 */
export const getProductForecast = (params = {}, scope) => get('stock-intelligence', 'product-forecast', params, scope, 'stock_prep_failed');

/**
 * Waste analysis.
 * @param {{days?: number, groupBy?: 'type'|'item', narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getWaste = (params = {}, scope) => get('stock-intelligence', 'waste', {days: 30, groupBy: 'type', ...params}, scope, 'stock_waste_failed');

/**
 * Theoretical-vs-actual variance.
 * @param {{days?: number, limit?: number, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getVariance = (params = {}, scope) => get('stock-intelligence', 'variance', {days: 30, ...params}, scope, 'stock_variance_failed');
